import React, {
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import NotificationContext from "../context/NotificationContext";
import { useMutationLock } from "../hooks/useMutationLock";
import * as financialAidApi from "../api/financialAid";
import * as tempAidApi from "../api/tempAid";
import "./FinancialAidModal.css";

const SECTIONS = [
  { id: "student", label: "Student" },
  { id: "father", label: "Father / guardian" },
  { id: "mother", label: "Mother" },
  { id: "household", label: "Household" },
  { id: "expenses", label: "Expenses & docs" },
  { id: "purpose", label: "Statement" },
];

const FATHER_MARITAL = ["married", "separated", "divorced", "widower"];
const MOTHER_MARITAL = ["married", "separated", "divorced", "widow"];
const FATHER_EMPLOYMENT = [
  "employeed",
  "self-employeed",
  "business",
  "unemployeed",
  "retired",
];
const MOTHER_EMPLOYMENT = [
  { value: "employeed", label: "Employeed" },
  { value: "self-employeed", label: "Self Employeed" },
  { value: "business", label: "Business" },
  { value: "unemployeed", label: "Unemployeed" },
  { value: "retired", label: "Retired" },
  { value: "house-wife", label: "House Wife" },
];
const LIVING_OPTIONS = [
  { value: "own-house", label: "Own House" },
  { value: "rental", label: "Rental" },
  { value: "lease", label: "Lease" },
];
const YES_NO = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

function titleCaseToken(value) {
  return String(value)
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

const AID_FILE_KEYS = [
  "cnicPicture",
  "fatherCnicPicture",
  "motherCnicPicture",
  "schoolFeesPicture",
  "electricityBillPicture",
  "guardianBankStatement",
  "bankStatement",
];

const AID_BOOL_KEYS = [
  "agriculturalLand",
  "additionalAssets",
  "availingLoans",
  "ownAccount",
];

function sanitizeStoredFile(value) {
  if (value == null) return null;
  if (typeof File !== "undefined" && value instanceof File) return value;
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (
    !trimmed ||
    trimmed === "null" ||
    trimmed === "undefined" ||
    trimmed === "[object Object]"
  ) {
    return null;
  }
  return trimmed;
}

function hasExistingFile(value) {
  return sanitizeStoredFile(value) != null;
}

function isFieldFilled(value) {
  if (value == null) return false;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (
      !trimmed ||
      trimmed === "null" ||
      trimmed === "undefined" ||
      trimmed === "[object Object]"
    ) {
      return false;
    }
    return true;
  }
  if (typeof File !== "undefined" && value instanceof File) return true;
  return Boolean(value);
}

function normalizeYesNo(value) {
  if (value === true || value === "true") return "true";
  if (value === false || value === "false") return "false";
  return "";
}

function AidFilePreview({ file, alt }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [fileName, setFileName] = useState("");
  const safeFile = sanitizeStoredFile(file);

  useEffect(() => {
    if (!safeFile) {
      setPreviewUrl(null);
      setFileName("");
      return undefined;
    }

    if (typeof safeFile === "string") {
      setPreviewUrl(safeFile);
      setFileName("");
      return undefined;
    }

    setFileName(safeFile.name || "Selected file");
    if (safeFile.type?.startsWith("image/")) {
      const objectUrl = URL.createObjectURL(safeFile);
      setPreviewUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }

    setPreviewUrl(null);
    return undefined;
  }, [safeFile]);

  if (!safeFile) return null;

  return (
    <div className="financial-aid-file__preview">
      {previewUrl ? (
        <img
          src={previewUrl}
          alt={alt}
          className="financial-aid-file__img"
        />
      ) : null}
      {fileName ? (
        <p className="financial-aid-file__name">Selected: {fileName}</p>
      ) : null}
      {hasExistingFile(file) ? (
        <p className="financial-aid-file__replace">
          Want to edit? Upload a new one
        </p>
      ) : null}
    </div>
  );
}

function TextField({
  id,
  label,
  hint,
  value,
  onChange,
  required = false,
  type = "text",
  className = "",
}) {
  return (
    <div className={`financial-aid-field ${className}`.trim()}>
      <label className="financial-aid-field__label" htmlFor={id}>
        {label}
      </label>
      {hint ? <p className="financial-aid-field__hint">{hint}</p> : null}
      <input
        id={id}
        type={type}
        className="financial-aid-input"
        value={value}
        onChange={onChange}
        required={required}
      />
    </div>
  );
}

function RadioField({
  legend,
  name,
  options,
  value,
  onChange,
  requiredFirst = true,
  className = "",
}) {
  return (
    <fieldset className={`financial-aid-field ${className}`.trim()}>
      <legend className="financial-aid-field__label">{legend}</legend>
      <div className="financial-aid-radios" role="radiogroup" aria-label={legend}>
        {options.map((option, index) => {
          const optionValue =
            typeof option === "string" ? option : option.value;
          const optionLabel =
            typeof option === "string"
              ? titleCaseToken(option)
              : option.label;
          const optionId = `${name}-${optionValue}`;
          return (
            <label key={optionValue} className="financial-aid-radio" htmlFor={optionId}>
              <input
                id={optionId}
                type="radio"
                name={name}
                value={optionValue}
                checked={value === optionValue}
                onChange={onChange}
                required={requiredFirst && index === 0}
              />
              <span>{optionLabel}</span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

function FileField({
  id,
  label,
  hint,
  file,
  onChange,
  requiredWhenMissing = true,
  forceRequired = false,
}) {
  const required =
    forceRequired ||
    (requiredWhenMissing && !hasExistingFile(file));

  return (
    <div className="financial-aid-field financial-aid-field--span2">
      <label className="financial-aid-field__label" htmlFor={id}>
        {label}
      </label>
      {hint ? <p className="financial-aid-field__hint">{hint}</p> : null}
      <div className="financial-aid-file">
        <AidFilePreview file={file} alt={label} />
        <input
          id={id}
          type="file"
          className="financial-aid-file__input"
          onChange={onChange}
          required={required}
        />
      </div>
    </div>
  );
}

function appendAidFormData(formData, values) {
  Object.entries(values).forEach(([key, value]) => {
    if (AID_FILE_KEYS.includes(key)) {
      const file = sanitizeStoredFile(value);
      // Empty string clears a previous bad/"null" draft value on the server
      formData.append(key, file ?? "");
      return;
    }

    if (AID_BOOL_KEYS.includes(key)) {
      // Only send an explicit choice; omit unset so drafts stay null (not false)
      if (value === "true" || value === "false") {
        formData.append(key, value);
      }
      return;
    }

    if (value == null) {
      formData.append(key, "");
      return;
    }

    formData.append(key, value);
  });
}

const FinancialAidModal = ({ userEmail, id, onDetailsSaved, onClose }) => {
  const titleId = useId();
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);
  const scrollRef = useRef(null);
  const handleCloseRef = useRef(null);
  const submittingRef = useRef(false);
  const pendingValidationStepRef = useRef(null);
  const { busy: submitting, run: runSubmit } = useMutationLock();

  const [email, setEmail] = useState(userEmail || "");
  const [userId, setUserId] = useState(id || "");
  const [studentName, setStudentName] = useState("");
  const [studentCnic, setStudentCnic] = useState("");
  const [studentPhoneNumber, setStudentPhoneNumber] = useState("");
  const [studentAddress, setStudentAddress] = useState("");
  const [cnicPicture, setCnicPicture] = useState(null);
  const [fatherName, setFatherName] = useState("");
  const [fatherCnic, setFatherCnic] = useState("");
  const [fatherPhoneNumber, setFatherPhoneNumber] = useState("");
  const [fatherMaritalStatus, setFatherMaritalStatus] = useState("");
  const [fatherAddress, setFatherAddress] = useState("");
  const [fatherEmploymentStatus, setFatherEmploymentStatus] = useState("");
  const [fatherOccupation, setFatherOccupation] = useState("");
  const [fatherCnicPicture, setFatherCnicPicture] = useState(null);
  const [motherName, setMotherName] = useState("");
  const [motherCnic, setMotherCnic] = useState("");
  const [motherPhoneNumber, setMotherPhoneNumber] = useState("");
  const [motherMaritalStatus, setMotherMaritalStatus] = useState("");
  const [motherAddress, setMotherAddress] = useState("");
  const [motherEmploymentStatus, setMotherEmploymentStatus] = useState("");
  const [motherCnicPicture, setMotherCnicPicture] = useState(null);
  const [livingArrangement, setLivingArrangement] = useState("");
  const [houseSize, setHouseSize] = useState("");
  const [noOfSibling, setNoOfSibling] = useState("");
  const [monthlySalary, setMonthlySalary] = useState("");
  const [agriculturalLand, setAgriculturalLand] = useState("");
  const [additionalAssets, setAdditionalAssets] = useState("");
  const [schoolFees, setSchoolFees] = useState("");
  const [schoolFeesPicture, setSchoolFeesPicture] = useState(null);
  const [tutionFees, setTutionFees] = useState("");
  const [transportCost, setTransportCost] = useState("");
  const [groceryExpense, setGroceryExpense] = useState("");
  const [electricityBill, setElectricityBill] = useState("");
  const [electricityBillPicture, setElectricityBillPicture] = useState(null);
  const [availingLoans, setAvailingLoans] = useState("");
  const [loanAmount, setLoanAmount] = useState("");
  const [repayTerms, setRepayTerms] = useState("");
  const [guardianBankStatement, setGuardianBankStatement] = useState(null);
  const [ownAccount, setOwnAccount] = useState("");
  const [bankStatement, setBankStatement] = useState(null);
  const [statementOfPurpose, setStatementOfPurpose] = useState("");
  const [loadingDraft, setLoadingDraft] = useState(true);
  const [activeSection, setActiveSection] = useState(SECTIONS[0].id);

  const notificationCtx = useContext(NotificationContext);
  const activeStepIndex = SECTIONS.findIndex(
    (section) => section.id === activeSection,
  );
  const isFirstStep = activeStepIndex <= 0;
  const isLastStep = activeStepIndex >= SECTIONS.length - 1;

  const completion = useMemo(() => {
    const countFields = (fields) => {
      const total = fields.length;
      const filled = fields.filter(isFieldFilled).length;
      return { filled, total };
    };

    const bySection = {
      student: countFields([
        studentName,
        studentCnic,
        studentPhoneNumber,
        studentAddress,
        cnicPicture,
      ]),
      father: countFields([
        fatherName,
        fatherCnic,
        fatherPhoneNumber,
        fatherMaritalStatus,
        fatherAddress,
        fatherEmploymentStatus,
        fatherOccupation,
        fatherCnicPicture,
      ]),
      mother: countFields([
        motherName,
        motherCnic,
        motherPhoneNumber,
        motherMaritalStatus,
        motherAddress,
        motherEmploymentStatus,
        motherCnicPicture,
      ]),
      household: countFields([
        livingArrangement,
        houseSize,
        noOfSibling,
        monthlySalary,
        agriculturalLand,
        additionalAssets,
      ]),
      expenses: countFields([
        schoolFees,
        schoolFeesPicture,
        tutionFees,
        transportCost,
        groceryExpense,
        electricityBill,
        electricityBillPicture,
        availingLoans,
        loanAmount,
        repayTerms,
        guardianBankStatement,
        ownAccount,
        ...(ownAccount === "true" ? [bankStatement] : []),
      ]),
      purpose: countFields([statementOfPurpose]),
    };

    const filled = Object.values(bySection).reduce(
      (sum, section) => sum + section.filled,
      0,
    );
    const total = Object.values(bySection).reduce(
      (sum, section) => sum + section.total,
      0,
    );
    const percent = total === 0 ? 0 : Math.round((filled / total) * 100);
    return { filled, total, percent, bySection };
  }, [
    studentName,
    studentCnic,
    studentPhoneNumber,
    studentAddress,
    cnicPicture,
    fatherName,
    fatherCnic,
    fatherPhoneNumber,
    fatherMaritalStatus,
    fatherAddress,
    fatherEmploymentStatus,
    fatherOccupation,
    fatherCnicPicture,
    motherName,
    motherCnic,
    motherPhoneNumber,
    motherMaritalStatus,
    motherAddress,
    motherEmploymentStatus,
    motherCnicPicture,
    livingArrangement,
    houseSize,
    noOfSibling,
    monthlySalary,
    agriculturalLand,
    additionalAssets,
    schoolFees,
    schoolFeesPicture,
    tutionFees,
    transportCost,
    groceryExpense,
    electricityBill,
    electricityBillPicture,
    availingLoans,
    loanAmount,
    repayTerms,
    guardianBankStatement,
    ownAccount,
    bankStatement,
    statementOfPurpose,
  ]);

  const handleFileChange = (e, setFile) => {
    setFile(e.target.files[0]);
  };

  const buildPayloadValues = () => ({
    email,
    userId,
    studentName,
    studentCnic,
    studentPhoneNumber,
    studentAddress,
    cnicPicture,
    fatherName,
    fatherCnic,
    fatherPhoneNumber,
    fatherMaritalStatus,
    fatherAddress,
    fatherEmploymentStatus,
    fatherOccupation,
    fatherCnicPicture,
    motherName,
    motherCnic,
    motherPhoneNumber,
    motherMaritalStatus,
    motherAddress,
    motherEmploymentStatus,
    motherCnicPicture,
    livingArrangement,
    houseSize,
    noOfSibling,
    monthlySalary,
    agriculturalLand,
    additionalAssets,
    schoolFees,
    schoolFeesPicture,
    tutionFees,
    transportCost,
    groceryExpense,
    electricityBill,
    electricityBillPicture,
    availingLoans,
    loanAmount,
    repayTerms,
    guardianBankStatement,
    ownAccount,
    bankStatement,
    statementOfPurpose,
  });

  const getStepFields = (stepId) => {
    const section = dialogRef.current?.querySelector(`#fa-section-${stepId}`);
    if (!section) return [];
    return Array.from(section.querySelectorAll("input, textarea, select"));
  };

  const isStepValid = (stepId) =>
    getStepFields(stepId).every((field) => field.checkValidity());

  const reportFirstInvalid = useCallback((stepId) => {
    const invalid = getStepFields(stepId).find(
      (field) => !field.checkValidity(),
    );
    if (!invalid) return;
    invalid.reportValidity();
    invalid.focus?.();
  }, []);

  const showStepValidation = (stepId) => {
    if (stepId === activeSection) {
      reportFirstInvalid(stepId);
      return;
    }
    pendingValidationStepRef.current = stepId;
    setActiveSection(stepId);
  };

  const goToStep = (stepId) => {
    const targetIndex = SECTIONS.findIndex((section) => section.id === stepId);
    if (targetIndex < 0) return;
    setActiveSection(stepId);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  };

  const goPrevious = () => {
    if (isFirstStep) return;
    goToStep(SECTIONS[activeStepIndex - 1].id);
  };

  const goNext = () => {
    if (isLastStep) return;
    goToStep(SECTIONS[activeStepIndex + 1].id);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    for (const section of SECTIONS) {
      if (!isStepValid(section.id)) {
        showStepValidation(section.id);
        return;
      }
    }

    await runSubmit(async () => {
      const formData = new FormData();
      appendAidFormData(formData, buildPayloadValues());

      try {
        await financialAidApi.create(formData);
        notificationCtx.show({
          message: "Financial aid details saved successfully!",
          onConfirm: () => onDetailsSaved(),
          stat: "success",
        });
      } catch (error) {
        console.error("Error saving financial aid details:", error);
        notificationCtx.show({
          message: "Failed to save financial aid details. Please try again.",
          stat: "error",
        });
      }
    });
  };

  const handleClose = async (e) => {
    e?.preventDefault?.();
    await runSubmit(async () => {
      const formData = new FormData();
      appendAidFormData(formData, buildPayloadValues());

      try {
        await tempAidApi.create(formData);
        onClose();
      } catch (error) {
        console.error("Error saving financial aid details:", error);
        notificationCtx.show({
          message: "Failed to save financial aid details. Please try again.",
          stat: "error",
        });
      }
    });
  };

  handleCloseRef.current = handleClose;
  submittingRef.current = submitting;

  useEffect(() => {
    if (pendingValidationStepRef.current !== activeSection) return;
    pendingValidationStepRef.current = null;
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    window.requestAnimationFrame(() => reportFirstInvalid(activeSection));
  }, [activeSection, reportFirstInvalid]);

  useEffect(() => {
    async function fetchDetails() {
      setLoadingDraft(true);
      try {
        const response = await tempAidApi.lookup({ email: userEmail });
        const data = response?.data;
        if (!data || data.message === "Record not found") {
          setEmail(userEmail || "");
          setUserId(id || "");
          return;
        }

        setEmail(userEmail || data.email || "");
        setUserId(id || data.userId || "");
        setStudentName(data.studentName || "");
        setStudentCnic(data.studentCnic || "");
        setStudentPhoneNumber(data.studentPhoneNumber || "");
        setStudentAddress(data.studentAddress || "");
        setCnicPicture(sanitizeStoredFile(data.cnicPicture));
        setFatherName(data.fatherName || "");
        setFatherCnic(data.fatherCnic || "");
        setFatherPhoneNumber(data.fatherPhoneNumber || "");
        setFatherMaritalStatus(data.fatherMaritalStatus || "");
        setFatherAddress(data.fatherAddress || "");
        setFatherEmploymentStatus(data.fatherEmploymentStatus || "");
        setFatherOccupation(data.fatherOccupation || "");
        setFatherCnicPicture(sanitizeStoredFile(data.fatherCnicPicture));
        setMotherName(data.motherName || "");
        setMotherCnic(data.motherCnic || "");
        setMotherPhoneNumber(data.motherPhoneNumber || "");
        setMotherMaritalStatus(data.motherMaritalStatus || "");
        setMotherAddress(data.motherAddress || "");
        setMotherEmploymentStatus(data.motherEmploymentStatus || "");
        setMotherCnicPicture(sanitizeStoredFile(data.motherCnicPicture));
        setLivingArrangement(data.livingArrangement || "");
        setHouseSize(data.houseSize || "");
        setNoOfSibling(data.noOfSibling || "");
        setMonthlySalary(data.monthlySalary || "");
        setAgriculturalLand(normalizeYesNo(data.agriculturalLand));
        setAdditionalAssets(normalizeYesNo(data.additionalAssets));
        setSchoolFees(data.schoolFees || "");
        setSchoolFeesPicture(sanitizeStoredFile(data.schoolFeesPicture));
        setTutionFees(data.tutionFees || "");
        setTransportCost(data.transportCost || "");
        setGroceryExpense(data.groceryExpense || "");
        setElectricityBill(data.electricityBill || "");
        setElectricityBillPicture(
          sanitizeStoredFile(data.electricityBillPicture),
        );
        setAvailingLoans(normalizeYesNo(data.availingLoans));
        setLoanAmount(data.loanAmount || "");
        setRepayTerms(data.repayTerms || "");
        setGuardianBankStatement(
          sanitizeStoredFile(data.guardianBankStatement),
        );
        setOwnAccount(normalizeYesNo(data.ownAccount));
        setBankStatement(sanitizeStoredFile(data.bankStatement));
        setStatementOfPurpose(data.statementOfPurpose || "");
      } catch (error) {
        console.error("Error fetching financial aid details:", error);
        notificationCtx.show({
          message: "Failed to fetch financial aid details. Please try again.",
          stat: "error",
        });
      } finally {
        setLoadingDraft(false);
      }
    }
    fetchDetails();
    // notificationCtx is stable enough for toast; avoid re-fetch loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail, id]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeBtnRef.current?.focus();

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        if (!submittingRef.current) handleCloseRef.current?.(e);
        return;
      }

      if (e.key !== "Tab" || !dialogRef.current) return;
      const focusable = dialogRef.current.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  return (
    <div className="financial-aid-overlay" role="presentation">
      <div
        ref={dialogRef}
        className="financial-aid-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="financial-aid-dialog__top">
          <div>
            <p className="financial-aid-dialog__eyebrow">Action needed</p>
            <h2 id={titleId} className="financial-aid-dialog__title">
              Complete your financial aid form
            </h2>
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            className="financial-aid-dialog__close"
            onClick={handleClose}
            disabled={submitting}
            aria-label="Save draft and close financial aid form"
          >
            ×
          </button>
        </div>

        <div
          className="financial-aid-completion"
          role="status"
          aria-live="polite"
          aria-label={`Form ${completion.percent}% complete`}
        >
          <div className="financial-aid-completion__meta">
            <span className="financial-aid-completion__label">
              Form completion
            </span>
            <span className="financial-aid-completion__value">
              {completion.percent}%
            </span>
          </div>
          <div
            className="financial-aid-completion__track"
            aria-hidden="true"
          >
            <div
              className="financial-aid-completion__fill"
              style={{ width: `${completion.percent}%` }}
            />
          </div>
        </div>

        <div className="financial-aid-dialog__layout">
          <aside className="financial-aid-aside">
            <p className="financial-aid-aside__lead">
              Partial-fee students must submit this form so admins can review
              your aid request. You can save a draft and finish later.
            </p>

            <div className="financial-aid-aside__meta">
              <span className="financial-aid-pill">Partial payment</span>
              <span className="financial-aid-pill financial-aid-pill--draft">
                Draft supported
              </span>
            </div>

            <p className="financial-aid-aside__progress" aria-live="polite">
              Step {Math.max(activeStepIndex, 0) + 1} of {SECTIONS.length}
            </p>

            <ul className="financial-aid-nav" aria-label="Form steps">
              {SECTIONS.map((section, index) => {
                const isActive = activeSection === section.id;
                const sectionProgress = completion.bySection[section.id] || {
                  filled: 0,
                  total: 0,
                };
                const isComplete =
                  sectionProgress.total > 0 &&
                  sectionProgress.filled === sectionProgress.total;
                return (
                  <li key={section.id}>
                    <button
                      type="button"
                      className={`financial-aid-nav__btn${
                        isActive ? " is-active" : ""
                      }${isComplete ? " is-complete" : ""}`}
                      onClick={() => goToStep(section.id)}
                      aria-current={isActive ? "step" : undefined}
                      aria-label={`${section.label}, ${sectionProgress.filled} of ${sectionProgress.total} fields completed`}
                    >
                      <span className="financial-aid-nav__index">
                        {index + 1}
                      </span>
                      <span className="financial-aid-nav__copy">
                        <span className="financial-aid-nav__label">
                          {section.label}
                        </span>
                        <span className="financial-aid-nav__count">
                          {sectionProgress.filled}/{sectionProgress.total}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>

            <p className="financial-aid-aside__note">
              After you submit, wait for an admin to review your form and
              contact you.
            </p>
          </aside>

          <div className="financial-aid-main">
            <form
              onSubmit={handleSubmit}
              className="financial-aid-form"
              noValidate
            >
              <div ref={scrollRef} className="financial-aid-form__scroll">
                {loadingDraft ? (
                  <p className="financial-aid-loading">
                    Loading your saved draft…
                  </p>
                ) : (
                  <>
                    <section
                      id="fa-section-student"
                      className={`financial-aid-section${
                        activeSection === "student" ? " is-active" : ""
                      }`}
                      aria-hidden={activeSection !== "student"}
                    >
                      <div className="financial-aid-section__head">
                        <h3 className="financial-aid-section__title">
                          Student personal information
                        </h3>
                      </div>
                      <div className="financial-aid-grid">
                        <div className="financial-aid-field financial-aid-field--span2">
                          <p className="financial-aid-field__label">Email</p>
                          <p className="financial-aid-field__readonly">
                            {email}
                          </p>
                        </div>
                        <TextField
                          id="fa-student-name"
                          label="Student's Full Name"
                          value={studentName}
                          onChange={(e) => setStudentName(e.target.value)}
                          required
                        />
                        <TextField
                          id="fa-student-cnic"
                          label="Student's CNIC Number"
                          value={studentCnic}
                          onChange={(e) => setStudentCnic(e.target.value)}
                          required
                        />
                        <TextField
                          id="fa-student-phone"
                          label="Student's Phone Number"
                          value={studentPhoneNumber}
                          onChange={(e) =>
                            setStudentPhoneNumber(e.target.value)
                          }
                          required
                        />
                        <TextField
                          id="fa-student-address"
                          label="Student's Complete Address"
                          value={studentAddress}
                          onChange={(e) => setStudentAddress(e.target.value)}
                          required
                          className="financial-aid-field--span2"
                        />
                        <FileField
                          id="fa-student-cnic-picture"
                          label="Student's National Identity Card/ BForm Picture"
                          file={cnicPicture}
                          onChange={(e) =>
                            handleFileChange(e, setCnicPicture)
                          }
                        />
                      </div>
                    </section>

                    <section
                      id="fa-section-father"
                      className={`financial-aid-section${
                        activeSection === "father" ? " is-active" : ""
                      }`}
                      aria-hidden={activeSection !== "father"}
                    >
                      <div className="financial-aid-section__head">
                        <h3 className="financial-aid-section__title">
                          Father / guardian information
                        </h3>
                      </div>
                      <div className="financial-aid-grid">
                        <TextField
                          id="fa-father-name"
                          label="Father's Full Name"
                          value={fatherName}
                          onChange={(e) => setFatherName(e.target.value)}
                          required
                        />
                        <TextField
                          id="fa-father-cnic"
                          label="Father's CNIC Number"
                          value={fatherCnic}
                          onChange={(e) => setFatherCnic(e.target.value)}
                          required
                        />
                        <FileField
                          id="fa-father-cnic-picture"
                          label="Father's CNIC Picture"
                          file={fatherCnicPicture}
                          onChange={(e) =>
                            handleFileChange(e, setFatherCnicPicture)
                          }
                        />
                        <TextField
                          id="fa-father-phone"
                          label="Father's Phone Number"
                          value={fatherPhoneNumber}
                          onChange={(e) =>
                            setFatherPhoneNumber(e.target.value)
                          }
                          required
                        />
                        <RadioField
                          legend="Father's Marital Status"
                          name="father-marital-status"
                          options={FATHER_MARITAL}
                          value={fatherMaritalStatus}
                          onChange={(e) =>
                            setFatherMaritalStatus(e.target.value)
                          }
                          className="financial-aid-field--span2"
                        />
                        <TextField
                          id="fa-father-address"
                          label="Father's Complete Address"
                          value={fatherAddress}
                          onChange={(e) => setFatherAddress(e.target.value)}
                          required
                          className="financial-aid-field--span2"
                        />
                        <RadioField
                          legend="Father's Employment Status"
                          name="father-employment-status"
                          options={FATHER_EMPLOYMENT}
                          value={fatherEmploymentStatus}
                          onChange={(e) =>
                            setFatherEmploymentStatus(e.target.value)
                          }
                          className="financial-aid-field--span2"
                        />
                        <TextField
                          id="fa-father-occupation"
                          label="Detailed description of father's occupation/business nature"
                          value={fatherOccupation}
                          onChange={(e) => setFatherOccupation(e.target.value)}
                          required
                          className="financial-aid-field--span2"
                        />
                      </div>
                    </section>

                    <section
                      id="fa-section-mother"
                      className={`financial-aid-section${
                        activeSection === "mother" ? " is-active" : ""
                      }`}
                      aria-hidden={activeSection !== "mother"}
                    >
                      <div className="financial-aid-section__head">
                        <h3 className="financial-aid-section__title">
                          Mother's information
                        </h3>
                      </div>
                      <div className="financial-aid-grid">
                        <TextField
                          id="fa-mother-name"
                          label="Mother's Full Name"
                          value={motherName}
                          onChange={(e) => setMotherName(e.target.value)}
                          required
                        />
                        <TextField
                          id="fa-mother-cnic"
                          label="Mother's CNIC Number"
                          value={motherCnic}
                          onChange={(e) => setMotherCnic(e.target.value)}
                          required
                        />
                        <FileField
                          id="fa-mother-cnic-picture"
                          label="Mother's CNIC Picture"
                          file={motherCnicPicture}
                          onChange={(e) =>
                            handleFileChange(e, setMotherCnicPicture)
                          }
                        />
                        <TextField
                          id="fa-mother-phone"
                          label="Mother's Phone Number"
                          value={motherPhoneNumber}
                          onChange={(e) =>
                            setMotherPhoneNumber(e.target.value)
                          }
                          required
                        />
                        <RadioField
                          legend="Mother's Marital Status"
                          name="mother-marital-status"
                          options={MOTHER_MARITAL}
                          value={motherMaritalStatus}
                          onChange={(e) =>
                            setMotherMaritalStatus(e.target.value)
                          }
                          className="financial-aid-field--span2"
                        />
                        <TextField
                          id="fa-mother-address"
                          label="Mother's Complete Address"
                          value={motherAddress}
                          onChange={(e) => setMotherAddress(e.target.value)}
                          required
                          className="financial-aid-field--span2"
                        />
                        <RadioField
                          legend="Mother's Employment Status"
                          name="mother-employment-status"
                          options={MOTHER_EMPLOYMENT}
                          value={motherEmploymentStatus}
                          onChange={(e) =>
                            setMotherEmploymentStatus(e.target.value)
                          }
                          className="financial-aid-field--span2"
                        />
                      </div>
                    </section>

                    <section
                      id="fa-section-household"
                      className={`financial-aid-section${
                        activeSection === "household" ? " is-active" : ""
                      }`}
                      aria-hidden={activeSection !== "household"}
                    >
                      <div className="financial-aid-section__head">
                        <h3 className="financial-aid-section__title">
                          Financial details
                        </h3>
                      </div>
                      <div className="financial-aid-grid">
                        <RadioField
                          legend="Living Arrangement"
                          name="living-arrangement"
                          options={LIVING_OPTIONS}
                          value={livingArrangement}
                          onChange={(e) =>
                            setLivingArrangement(e.target.value)
                          }
                          className="financial-aid-field--span2"
                        />
                        <TextField
                          id="fa-house-size"
                          label="What is the size of the house you are living in?"
                          hint="Please give your answer in marlas or kanals etc."
                          value={houseSize}
                          onChange={(e) => setHouseSize(e.target.value)}
                          required
                          className="financial-aid-field--span2"
                        />
                        <TextField
                          id="fa-siblings"
                          label="Total Number of Siblings"
                          value={noOfSibling}
                          onChange={(e) => setNoOfSibling(e.target.value)}
                          required
                        />
                        <TextField
                          id="fa-monthly-salary"
                          label="Monthly Salary (Net Salary)"
                          value={monthlySalary}
                          onChange={(e) => setMonthlySalary(e.target.value)}
                          required
                        />
                        <RadioField
                          legend="Does your family own agricultural land?"
                          name="agricultural-land"
                          options={YES_NO}
                          value={agriculturalLand}
                          onChange={(e) =>
                            setAgriculturalLand(e.target.value)
                          }
                        />
                        <RadioField
                          legend="Does your family own additional assets (plot, house, shop, etc.)?"
                          name="additional-assets"
                          options={YES_NO}
                          value={additionalAssets}
                          onChange={(e) =>
                            setAdditionalAssets(e.target.value)
                          }
                        />
                      </div>
                    </section>

                    <section
                      id="fa-section-expenses"
                      className={`financial-aid-section${
                        activeSection === "expenses" ? " is-active" : ""
                      }`}
                      aria-hidden={activeSection !== "expenses"}
                    >
                      <div className="financial-aid-section__head">
                        <h3 className="financial-aid-section__title">
                          Expenses & supporting documents
                        </h3>
                      </div>
                      <div className="financial-aid-grid">
                        <TextField
                          id="fa-school-fees"
                          label="Monthly School Fees (Yourself)"
                          value={schoolFees}
                          onChange={(e) => setSchoolFees(e.target.value)}
                          required
                        />
                        <TextField
                          id="fa-tuition-fees"
                          label="Monthly Tuition Fee (Sibling, if applicable)"
                          value={tutionFees}
                          onChange={(e) => setTutionFees(e.target.value)}
                          required
                        />
                        <FileField
                          id="fa-school-fees-picture"
                          label="LATEST Monthly School Fee paid (Yourself) Picture"
                          file={schoolFeesPicture}
                          onChange={(e) =>
                            handleFileChange(e, setSchoolFeesPicture)
                          }
                        />
                        <TextField
                          id="fa-transport"
                          label="Monthly transport cost to and from school"
                          value={transportCost}
                          onChange={(e) => setTransportCost(e.target.value)}
                          required
                        />
                        <TextField
                          id="fa-grocery"
                          label="Monthly Food & Grocery expenses"
                          value={groceryExpense}
                          onChange={(e) => setGroceryExpense(e.target.value)}
                          required
                        />
                        <TextField
                          id="fa-electricity"
                          label="Latest Electricity Bill"
                          value={electricityBill}
                          onChange={(e) => setElectricityBill(e.target.value)}
                          required
                        />
                        <FileField
                          id="fa-electricity-picture"
                          label="Latest Electricity Bill Picture"
                          file={electricityBillPicture}
                          onChange={(e) =>
                            handleFileChange(e, setElectricityBillPicture)
                          }
                        />
                        <RadioField
                          legend="Is your family availing any loan(s)?"
                          name="availing-loans"
                          options={YES_NO}
                          value={availingLoans}
                          onChange={(e) => setAvailingLoans(e.target.value)}
                          className="financial-aid-field--span2"
                        />
                        <TextField
                          id="fa-loan-amount"
                          label="Loan Source and amount details"
                          value={loanAmount}
                          onChange={(e) => setLoanAmount(e.target.value)}
                          required
                        />
                        <TextField
                          id="fa-repay-terms"
                          label="Repayment Terms"
                          value={repayTerms}
                          onChange={(e) => setRepayTerms(e.target.value)}
                          required
                        />
                        <FileField
                          id="fa-guardian-bank"
                          label="Latest six months bank statement of your GUARDIAN's account"
                          hint="Attach the statement that clearly shows your monthly source of income."
                          file={guardianBankStatement}
                          onChange={(e) =>
                            handleFileChange(e, setGuardianBankStatement)
                          }
                        />
                        <RadioField
                          legend="Do you have a bank account?"
                          name="own-account"
                          options={YES_NO}
                          value={ownAccount}
                          onChange={(e) => setOwnAccount(e.target.value)}
                          requiredFirst
                          className="financial-aid-field--span2"
                        />
                        <FileField
                          id="fa-own-bank"
                          label="Latest six months bank statement of your bank account"
                          file={bankStatement}
                          onChange={(e) =>
                            handleFileChange(e, setBankStatement)
                          }
                          requiredWhenMissing={false}
                          forceRequired={
                            ownAccount === "true" &&
                            !hasExistingFile(bankStatement)
                          }
                        />
                      </div>
                    </section>

                    <section
                      id="fa-section-purpose"
                      className={`financial-aid-section${
                        activeSection === "purpose" ? " is-active" : ""
                      }`}
                      aria-hidden={activeSection !== "purpose"}
                    >
                      <div className="financial-aid-section__head">
                        <h3 className="financial-aid-section__title">
                          Statement of purpose
                        </h3>
                      </div>
                      <p className="financial-aid-section__hint">
                        Please use this portion to explain any extraordinary
                        circumstances that the financial aid committee will find
                        useful to take a decision for you.
                      </p>
                      <div className="financial-aid-grid financial-aid-grid--full">
                        <div className="financial-aid-field">
                          <label
                            className="financial-aid-field__label"
                            htmlFor="fa-statement"
                          >
                            Statement of Purpose
                          </label>
                          <textarea
                            id="fa-statement"
                            className="financial-aid-textarea"
                            value={statementOfPurpose}
                            onChange={(e) =>
                              setStatementOfPurpose(e.target.value)
                            }
                          />
                        </div>
                      </div>
                    </section>
                  </>
                )}
              </div>

              <div className="financial-aid-footer">
                <button
                  type="button"
                  className="financial-aid-btn financial-aid-btn--ghost"
                  disabled={submitting || loadingDraft}
                  onClick={handleClose}
                >
                  {submitting ? "Working…" : "Save draft & close"}
                </button>
                <div className="financial-aid-footer__nav">
                  <button
                    type="button"
                    className="financial-aid-btn financial-aid-btn--ghost"
                    disabled={submitting || loadingDraft || isFirstStep}
                    onClick={goPrevious}
                  >
                    Previous
                  </button>
                  {isLastStep ? (
                    <button
                      type="submit"
                      className="financial-aid-btn financial-aid-btn--primary"
                      disabled={submitting || loadingDraft}
                    >
                      {submitting ? "Working…" : "Submit"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="financial-aid-btn financial-aid-btn--primary"
                      disabled={submitting || loadingDraft}
                      onClick={goNext}
                    >
                      Next
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialAidModal;
